import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Map, MapPin, LogOut, ShieldCheck, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import '../styles/Navigation.css';

function Navigation({ admin = false, lapsed = false }: { admin?: boolean; lapsed?: boolean }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const prevCount = useRef(-1);

  // Soft two-note chime when a new check-in arrives. Fails silently if the
  // browser has not allowed sound yet.
  const ding = () => {
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new Ctx();
      [660, 880].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.18);
        gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + i * 0.18 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.18 + 0.3);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.18);
        osc.stop(ctx.currentTime + i * 0.18 + 0.35);
      });
    } catch {
      // no sound available, badge still shows
    }
  };

  useEffect(() => {
    if (!admin) return;
    let stopped = false;
    const check = async () => {
      const { count } = await supabase
        .from('visits')
        .select('id', { count: 'exact', head: true })
        .eq('verified', false);
      if (stopped || count === null || count === undefined) return;
      if (prevCount.current >= 0 && count > prevCount.current) {
        ding();
      }
      prevCount.current = count;
      setPendingCount(count);
    };
    check();
    const timer = setInterval(check, 60000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [admin]);

  const handleLogout = async () => {
    setConfirmOpen(false);
    await supabase.auth.signOut();
  };

  return (
    <>
      <nav className="bottom-nav">
        <NavLink to="/" end className="nav-item">
          <Home size={22} />
          <span>My Stamps</span>
        </NavLink>
        <NavLink to="/map" className="nav-item">
          <Map size={22} />
          <span>Map</span>
        </NavLink>
        <NavLink to="/checkin" className="nav-item">
          <MapPin size={22} />
          <span>Check In</span>
        </NavLink>
        {lapsed && (
          <NavLink to="/join" className="nav-item">
            <RefreshCw size={22} />
            <span>Renew</span>
          </NavLink>
        )}
        {admin && (
          <NavLink to="/admin" className="nav-item">
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <ShieldCheck size={22} />
              {pendingCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-10px',
                    minWidth: '18px',
                    height: '18px',
                    borderRadius: '9px',
                    background: '#d93025',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                    boxSizing: 'border-box',
                    lineHeight: 1,
                  }}
                >
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              )}
            </span>
            <span>Admin</span>
          </NavLink>
        )}
        <button
          className="nav-item nav-logout"
          onClick={() => setConfirmOpen(true)}
        >
          <LogOut size={22} />
          <span>Log Out</span>
        </button>
      </nav>
      {confirmOpen && (
        <div
          onClick={() => setConfirmOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(27, 46, 75, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '320px',
              textAlign: 'center',
            }}
          >
            <p
              style={{
                color: '#1B2E4B',
                fontSize: '17px',
                fontWeight: 600,
                margin: '0 0 4px',
              }}
            >
              Log out?
            </p>
            <p
              style={{
                color: '#5a6b85',
                fontSize: '14px',
                margin: '0 0 20px',
              }}
            >
              You will need to sign in again to see your stamps.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setConfirmOpen(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#ffffff',
                  color: '#1B2E4B',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#1B2E4B',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navigation;
